import { useEffect, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'sonner';
import { initI18n, i18n } from '@/shared/i18n';
import { bootstrapSession } from '@/shared/auth/api';

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Auth bootstrap is bounded so a hanging /api/v3/auth/me cannot pin the
      // splash — the worst case is the user lands on /login and logs
      // in fresh, which is the same outcome as a real 401.
      try {
        await Promise.race([
          bootstrapSession(),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (err) {
        console.warn('[boot] auth bootstrap failed', err);
      }

      // Переводы — после сессии и до первого кадра. Именно порядок делал вид
      // «словарь докатывается»: пространство имён словаря включает
      // DistrID/ClientID, и запрос до сессии уходил с нулями, то есть впустую,
      // а настоящий словарь начинал грузиться только после /auth/me — уже
      // поверх нарисованных `DTK_*`. Ожидание ограничено: 132 КБ словаря
      // приезжают за ~70 мс, но зависший бекенд не должен держать заглушку, а
      // подставленный из кеша словарь снимает ожидание вовсе.
      try {
        await Promise.race([
          initI18n(),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]);
      } catch (err) {
        console.warn('[boot] i18n init failed; using defaults', err);
      }

      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return <BootSplash />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        {children}
        <Toaster richColors position="top-right" closeButton />
      </I18nextProvider>
    </QueryClientProvider>
  );
}

function BootSplash() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );
}
